import { Fragment } from 'react';
import { Box, Link as MuiLink, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

/**
 * `<CitationLine>` — renders the "read more" citation footer for a
 * news / release / fact item.
 *
 * Round-26 refinements over the pre-round-26 flat "Read more at X, Y
 * and Z" line:
 *   1. Deduplicates sources by name + URL so the same outlet never
 *      appears twice on one line.
 *   2. Rotates through varied contextual phrasings, keyed on a
 *      stable hash of the entry id so a given item always renders
 *      with the same phrasing.
 *   3. Optionally interleaves references to games mentioned in the
 *      item, each hyperlinked to `/games/:gameId`.
 *
 * Function-length budget: every export below stays under 20 lines
 * per Requirement 3.7.
 */

export interface CitationSource {
  readonly name: string;
  readonly url: string;
}

export interface MentionedGame {
  readonly gameId: string;
  readonly title: string;
}

export interface CitationLineProps {
  /** Stable hash key — usually the item's id or index in the list. */
  readonly hashKey: string;
  /** Ordered list of contributing sources; deduplicated internally. */
  readonly sources: ReadonlyArray<CitationSource>;
  /** Optional games referenced in the body; rendered as game-page links. */
  readonly mentionedGames?: ReadonlyArray<MentionedGame>;
}

/**
 * Deduplicate sources by trimmed URL first, then normalised name.
 * Preserves first-encountered order so the citation reads left-to-right
 * consistently with the composer's ranking.
 */
function dedupeSources(sources: ReadonlyArray<CitationSource>): ReadonlyArray<CitationSource> {
  const seen = new Set<string>();
  const out: CitationSource[] = [];
  for (const s of sources) {
    const url = (s.url ?? '').trim();
    const name = (s.name ?? '').trim().toLowerCase();
    const key = url.length > 0 ? `url:${url}` : `name:${name}`;
    if (seen.has(key)) continue;
    if (url.length === 0 && name.length === 0) continue;
    seen.add(key);
    out.push({ name: (s.name ?? '').trim(), url });
  }
  return out;
}

/** Deterministic 0..n-1 hash from a string using DJB2. */
function stableIndex(input: string, modulus: number): number {
  if (modulus <= 0) return 0;
  let hash = 5381;
  for (let i = 0; i < input.length; i++) hash = ((hash << 5) + hash + input.charCodeAt(i)) | 0;
  return Math.abs(hash) % modulus;
}

/** Six varied "read more" leading phrasings. */
const LEADING_PHRASES: ReadonlyArray<string> = [
  'Read more at ',
  'Coverage from ',
  'Full details from ',
  'Additional reporting at ',
  'More at ',
  'The story is also up at ',
];

/** Trailing phrasings paired with the leading ones by hash. */
const TRAILING_PHRASES: ReadonlyArray<string> = [
  '.',
  ' has the details.',
  ' covers the full breakdown.',
  '.',
  ' — worth a read.',
  '.',
];

/**
 * Render a list of sources with proper `, ` / ` and ` joiners.
 */
function renderSourceList(
  sources: ReadonlyArray<CitationSource>,
): JSX.Element {
  return (
    <>
      {sources.map((src, idx) => {
        const label = src.name.length > 0 ? src.name : 'source';
        const joiner = idx === 0 ? '' : idx === sources.length - 1 ? ' and ' : ', ';
        return (
          <Fragment key={`${label}-${idx}-${src.url}`}>
            {joiner}
            {src.url.length > 0 ? (
              <MuiLink href={src.url} rel="noopener noreferrer" target="_blank">
                {label}
              </MuiLink>
            ) : (
              <Box component="span">{label}</Box>
            )}
          </Fragment>
        );
      })}
    </>
  );
}

/**
 * Render the mentioned-games trailer clause. Rendered only when
 * `mentionedGames` is present and non-empty. Each game name links
 * to `/games/:gameId` via React Router.
 */
function renderGamesClause(games: ReadonlyArray<MentionedGame>): JSX.Element | null {
  if (games.length === 0) return null;
  return (
    <>
      {' If you want more on the games themselves, check '}
      {games.map((g, idx) => {
        const joiner = idx === 0 ? '' : idx === games.length - 1 ? ' and ' : ', ';
        return (
          <Fragment key={`${g.gameId}-${idx}`}>
            {joiner}
            <MuiLink component={RouterLink} to={`/games/${g.gameId}`}>
              {g.title}
            </MuiLink>
          </Fragment>
        );
      })}
      .
    </>
  );
}

export function CitationLine(props: CitationLineProps): JSX.Element | null {
  const sources = dedupeSources(props.sources);
  if (sources.length === 0) return null;
  const phraseIndex = stableIndex(props.hashKey, LEADING_PHRASES.length);
  const lead = LEADING_PHRASES[phraseIndex];
  const tail = TRAILING_PHRASES[phraseIndex];
  const games = props.mentionedGames ?? [];
  return (
    <Typography variant="body1" sx={{ mt: 1 }}>
      {lead}
      {renderSourceList(sources)}
      {tail}
      {renderGamesClause(games)}
    </Typography>
  );
}
